import { useNavigate } from '@tanstack/react-router';
import { Icon, IconButton, ProgressBar } from '@yembo/yemblocks-core';
import { useContext, useRef } from 'react';
import Draggable from 'react-draggable';
import { ItemImportContext } from '../Context/ItemImport/context';

/**
 * Global, job-agnostic view of the one item import the app can have
 * running at a time — shown whenever it's minimized, from anywhere in the
 * app, so a multi-minute spreadsheet import doesn't have to be babysat on
 * its own job's screen. See ItemImportModal for the full setup/review UI
 * this steps in for; see ItemImportProvider for the run itself.
 *
 * A spreadsheet only ever shows here while it is uploading: once the server
 * has it, the provider hands it to the job's `ImportProgressBanner` (reading,
 * review, pricing), so a job page never shows two progress UIs for one sheet.
 */
export const ItemImportProgressToast = () => {
  const navigate = useNavigate();
  const draggableRef = useRef<HTMLDivElement>(null);

  const { jobKey, phase, processed, total, found, candidates, isMinimized, restore, cancel } =
    useContext(ItemImportContext);

  const isVisible = isMinimized && jobKey !== null && phase !== 'idle';
  if (!isVisible) return null;

  const goToReview = () => {
    restore();
    navigate({ to: '/job/$key/inventory/{-$roomKey}/{-$clipKey}', params: { key: jobKey } });
  };

  const { title, detail, showSpinner } = (() => {
    if (phase === 'committing') {
      return { title: 'Adding your items', detail: null, showSpinner: true };
    }
    if (phase === 'ready') {
      const count = candidates.length;
      return { title: `${count} ${count === 1 ? 'item' : 'items'} ready to review`, detail: null, showSpinner: false };
    }
    if (phase === 'failed') {
      return { title: 'Import failed', detail: 'Open it to see what happened', showSpinner: false };
    }
    // uploading / extracting
    return {
      title: 'Importing spreadsheet',
      detail: found > 0 ? `${found} ${found === 1 ? 'item' : 'items'} found so far` : null,
      showSpinner: true,
    };
  })();

  // Photos/lists have a real, known total to show a percentage against; a
  // spreadsheet's `total` is always 1 (it's one source file), so `found`
  // (text only, no bar — see ItemImportModal) is the honest signal instead.
  const showFileProgress = (phase === 'uploading' || phase === 'extracting') && total > 1;

  return (
    <Draggable handle='.imp-toast' bounds='parent' nodeRef={draggableRef} cancel='.imp-toast-close, .imp-toast-body'>
      <div className='imp-toast' ref={draggableRef}>
        <button type='button' className='imp-toast-body' onClick={goToReview}>
          {showSpinner && <div className='imp-toast-spinner' />}
          <span className='imp-toast-text'>
            <span className='imp-toast-title'>{title}</span>
            {detail && <span className='imp-toast-detail'>{detail}</span>}
          </span>
        </button>

        {showFileProgress && <ProgressBar valueCurrent={processed} valueMax={total} className='imp-toast-progress' />}

        <IconButton
          className='imp-toast-close'
          kind='flat'
          size='large'
          icon={<Icon name='x' />}
          aria-label='Cancel import'
          onClick={cancel}
        />
      </div>
    </Draggable>
  );
};
